import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * Ajuda contextual de um indicador: rótulo, fórmula, unidade, período,
 * denominador e regra de inclusão (AGENTS §10).
 *
 * Visual do protótipo Zênite: o "?" redondo (`.info`) que abre um painel
 * ancorado. É um popover NÃO modal — por isso não passa por `useModalOverlay`,
 * que é exclusivo de diálogos modais. Ainda assim fecha por Escape e por clique
 * fora, e devolve o foco ao gatilho.
 */
export interface MetricExplanation {
  term: string;
  description: ReactNode;
}

export interface HelpPopoverProps {
  /** O que está sendo explicado; entra no nome acessível do gatilho. */
  about: string;
  items: MetricExplanation[];
  /** Nome acessível completo, quando "Como X é calculado" não descreve o caso. */
  label?: string;
  /** Alinha o painel pela borda direita quando o "?" fica no fim da linha. */
  align?: "start" | "end";
  className?: string;
}

export function HelpPopover({ about, items, label, align = "start", className }: HelpPopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const name = label ?? `Como ${about} é calculado`;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <span ref={rootRef} className={className ? `z-info-wrap ${className}` : "z-info-wrap"}>
      <button
        ref={triggerRef}
        type="button"
        className="info"
        aria-label={name}
        title={name}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        ?
      </button>
      {open && (
        <dl id={panelId} className="z-info-pop" data-align={align === "end" ? "end" : undefined}>
          {items.map((item) => (
            <div key={item.term}>
              <dt>{item.term}</dt>
              <dd>{item.description}</dd>
            </div>
          ))}
        </dl>
      )}
    </span>
  );
}
